import { Injectable, inject } from '@angular/core';
import { IBizEntity, IBizEntityType, BizEntityTypeCategory, BizInformationTypes, FieldTypes, LoggerService } from '@optima-stride-ai/core';
import { IListView, IListViewColHeader, IListViewItem, EventSources, ExplorerInterceptsFor } from '@optima-stride-ai/optima-components';
import { UILabels, URLTemplates, Routes } from '../../../shared/enums/app-constants';
import { CollaboraService } from '../../../services/collabora.service';
import { environment } from '../../../../environments/environment';
import { getAvatarEle } from '../../../services/utility';
import { EntityLayoutConfigService } from '../../../services/entity-layout-config.service';
import { AppTagComponent,  AppTagService, AssignedTagsByEntityId } from '@optima-stride-ai/optima-app-framework';
import { Router } from '@angular/router';
import { AdditionalFieldNames } from '../../../../enum/additional-fields';

enum TemplateListViewKeys {
  COLUMN = 'isTemplate'
}

/**
 * Service responsible for transforming library data into formats
 * required by the Explorer component (list views, grid views, etc.)
 */
@Injectable({
  providedIn: 'root'
})
export class LibraryDataTransformerService {
  private readonly collaboraService = inject(CollaboraService);
  private readonly logger = inject(LoggerService);
  private readonly entityLayoutConfigService = inject(EntityLayoutConfigService);
  private readonly router = inject(Router);
    private readonly appTagService = inject(AppTagService);

  /**
   * Build list view from entity data
   */

   private getTagData(entities: IBizEntity[]): Promise<{
    availableTagsFromAPI: IBizEntity[];
    tagBizEntityTypeFromAPI: IBizEntityType | null;
    parentAssignedTagsFromAPI: Record<string, IBizEntity[]>;
  }> {
    const entityIds = entities
      .map(entity => entity.id);

    return Promise.all([
      this.appTagService.getAvailableTags(),
      this.appTagService.getSystemTagBizEntityType(),
      entityIds.length > 0
        ? this.appTagService.getAssignedTags(entityIds)
        : Promise.resolve({} as Record<string, IBizEntity[]>),
    ]).then(([availableTagsFromAPI, tagBizEntityTypeFromAPI, parentAssignedTagsFromAPI]) => {
      // console.log('availableTagsFromAPI', availableTagsFromAPI);
      // console.log('tagBizEntityTypeFromAPI', tagBizEntityTypeFromAPI);
      // console.log('parentAssignedTagsFromAPI', parentAssignedTagsFromAPI);
      return {
        availableTagsFromAPI,
        tagBizEntityTypeFromAPI,
        parentAssignedTagsFromAPI,
      };
    });
  }


  async buildListView(
    data: IBizEntity[],
    onContextMenu: (item: IListViewItem<IBizEntity> | undefined, event?: Event | undefined) => void,
    onDrillDown: (itemId: string) => void,
    onSelectItem: (itemId: string) => void,
    onOpenInViewer: (entity: IBizEntity) => void
  ): Promise<IListView<IBizEntity>> {
    const tagData = await this.getTagData(data);
    const listItems = this.getListViewItems(
      data,
      onContextMenu,
      onDrillDown,
      onSelectItem,
      onOpenInViewer,
      false,
      tagData
    ) as IListViewItem<IBizEntity>[];
    const isTemplatePage = this.isTemplatesRoute();
    const colHeaders: (IListViewColHeader & { isTemplate?: boolean })[] = [
      { key: 'col1', label: '', icon: '' },
      { key: 'col2', label: UILabels.NAME, icon: 'cilFile', align: 'left' },
      { key: 'col3', label: UILabels.TYPE, icon: 'cilTag', align: 'left' },
      { key: 'col4', label: UILabels.TAG, icon: 'cilTag', align: 'right' },
      { key: 'col7', label: UILabels.OWNER, icon: 'cilUser', align: 'center' }
    ];

    if (isTemplatePage) {
      colHeaders.splice(3, 0, {
        key: TemplateListViewKeys.COLUMN,
        label: UILabels.TEMPLATE,
        icon: 'cilCheckAlt',
        isTemplate: true
      });
    }

    return {
      title: 'Library Items',
      colHeaders: colHeaders,
      items: listItems
    };
  }

  /**
   * Convert entity data to list view items for Explorer
   */
  getListViewItems(
    data: IBizEntity[],
    contextMenu: (item: IListViewItem<IBizEntity> | undefined, event?: Event | undefined) => void,
    drillDown: (itemId: string) => void,
    selectClickedItem: (itemId: string) => void,
    openInViewer: (entity: IBizEntity) => void,
    isInModal: boolean,
    tagData?: { availableTagsFromAPI: IBizEntity[]; tagBizEntityTypeFromAPI: IBizEntityType | null; parentAssignedTagsFromAPI: Record<string, IBizEntity[]> }
  ): IListViewItem<IBizEntity>[] {
    const items: IListViewItem<IBizEntity>[] = [];
    const isTemplatePage = this.isTemplatesRoute();

    for (let i = 0; i < data.length; i++) {
      const entity: IBizEntity = data[i];
      const stableId = entity.id || `item-${i}`;

      const item: IListViewItem<IBizEntity> = {
        id: stableId,
        itemActions: [
          {
            label: 'selectItem',
            icon: '',
            tooltip: '',
            action: (item: IListViewItem<IBizEntity> | undefined) => {
              if (item?.id) {
                selectClickedItem(item.id);
              }
            },
            eventSource: EventSources.Click
          },
          {
            label: 'itemClicked',
            icon: '',
            tooltip: '',
            action: (item: IListViewItem<IBizEntity> | undefined) => {
              if (item?.id) {
                const entityData = data.find(e => e.id === item.id);
                if (entityData) {
                  this.handleItemClick(entityData, drillDown, openInViewer, item.id);
                }
              }
            },
            eventSource: EventSources.DoubleClick,
            interceptableFor: ExplorerInterceptsFor.HierarchyItemSelection
          }
        ],
        columns: [
          {
            key: 'col1',
            label: 'L1',
            value: entity.icon || 'cilFile',
            metaData: { name: 'col1', label: 'L1', type: FieldTypes.text },
            bizInformationType: BizInformationTypes.icon,
            align: 'center'
          },
          {
            key: 'col2',
            label: 'L2',
            value: entity.name || 'Unnamed Item',
            metaData: { name: 'col2', label: 'L2', type: FieldTypes.text },
            bizInformationType: BizInformationTypes.text,
            align: 'left'
          },
          {
            key: 'col3',
            label: 'L3',
            value: entity?.type?.name || '',
            metaData: { name: 'col3', label: 'L3', type: FieldTypes.text },
            bizInformationType: BizInformationTypes.text,
            align: 'left'
          },
          {
            key: 'col4',
            label: 'L4',
            value: { component: AppTagComponent, inputs: {
              getAvailableTags: tagData?.availableTagsFromAPI ?? [],
              getAssignedTags: tagData?.parentAssignedTagsFromAPI ?? {},
              tagBizEntityType: tagData?.tagBizEntityTypeFromAPI ?? null,
              bizEntity: entity,
              addNew: true,
            } },
            metaData: { name: 'col4', label: 'L4', type: FieldTypes.component },
            bizInformationType: BizInformationTypes.component,
            align: 'right',
          },
          {
            key: 'col7',
            label: 'L7',
            value: entity.ownerDetail?.avatar || URLTemplates.DEFAULT_AVATAR,
            metaData: { name: 'col7', label: 'L7', type: FieldTypes.text },
            bizInformationType: BizInformationTypes.image,
            align: 'center',
            optimaHTMLElement: getAvatarEle()
          }
        ],
        actions: !isInModal ? [
          {
            label: 'Menu',
            icon: 'cilOptions',
            tooltip: 'Action',
            action: (item: unknown, event?: Event) => {
              contextMenu(item as IListViewItem<IBizEntity> | undefined, event)
            },
            interceptableFor: ExplorerInterceptsFor.ContextMenu
          }
        ] : [],
        // Use the global EntityLayoutConfigService for consistent layout across all views
        // This ensures dashboard and libraries look identical but with size constraints for library
        layout: this.entityLayoutConfigService.getLayoutConfigForExplorer(entity)
      };

      if (isTemplatePage) {
        item.columns?.splice(3, 0, {
          key: TemplateListViewKeys.COLUMN,
          label: UILabels.TEMPLATE,
          value: this.isTemplateEntity(entity) ? "True" : "",
          metaData: {
            name: TemplateListViewKeys.COLUMN,
            label: UILabels.TEMPLATE,
            type: FieldTypes.text
          },
          bizInformationType: BizInformationTypes.text
        });
      }

      items.push(item);
    }

    return items;
  }

  /**
   * Handle item click - determine if it should drill down or open in viewer
   */
  private async handleItemClick(
    entityData: IBizEntity,
    drillDown: (itemId: string) => void,
    openInViewer: (entity: IBizEntity) => void,
    itemId: string
  ): Promise<void> {
    this.logger.debug(`Double clicked item category: ${entityData?.type?.name || 'unknown'}`);

    // Check if this should open in a viewer
    if (this.shouldOpenInViewer(entityData)) {
      //        const res = await this.relationshipState.getState([entityData!.id], undefined,undefined, undefined, true, undefined)
      openInViewer(entityData);
      return;
    }

    // Default drill down behavior for folders
    this.logger.debug(`Drilling down into item: ${itemId}`);
    drillDown(itemId);
  }

  /**
   * Check if entity should open in a viewer
   */
  private shouldOpenInViewer(entity: IBizEntity): boolean {
    const category = entity?.type?.category;

    // Image files
    if (category === BizEntityTypeCategory.HayagrivaFileImage) {
      return true;
    }

    // Video files
    if (category === BizEntityTypeCategory.HayagrivaFileVideo) {
      return true;
    }

    // PDF files
    if (category === BizEntityTypeCategory.HayagrivaFilePDF) {
      return true;
    }

    // Markdown files
    if (category === BizEntityTypeCategory.HayagrivaFileMarkdown) {
      return true;
    }

    // Whiteboard files
    if (category === BizEntityTypeCategory.HayagrivaFileWhiteboard) {
      return true;
    }

    // Collabora files
    if (environment.collaboraEnabled) {
      // const isSupported = this.collaboraService.isFileSupported(entity.name);
      const isSupported = this.collaboraService.isFileSupported(category);
      if (isSupported) {
        return true;
      }
    }

    return false;
  }

  private isTemplatesRoute(): boolean {
    return this.router.url.includes(Routes.DOCUMENTS_TEMPLATES);
  }

  private isTemplateEntity(entity: IBizEntity): boolean {
    const additionalInfo = entity.additionalInfo as Array<{ fieldMeta?: { name?: string }; value?: unknown }> | undefined;
    if (!additionalInfo?.length) {
      return false;
    }

    return additionalInfo.some(info =>
      info?.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
      String(info?.value).toLowerCase() === 'true'
    );
  }

  /**
   * Normalize entity data to ensure consistent object identity
   */
  normalizeEntityData(data: IBizEntity[]): IBizEntity[] {
    return data.map((item, index) => ({
      ...item,
      id: item.id || `normalized-${index}`
    }));
  }
  /**
   * Generate UUID for refresh tokens
   */
  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
